import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Handshake, Users, Building2, Briefcase, CalendarDays, FileText, BarChart3, Settings, LogOut, Send, Grid3x3, StickyNote, Sparkles, Megaphone, LineChart, PiggyBank, ClipboardList, Wallet, Film, FileEdit, Database, Mail } from 'lucide-react';
import Logo from './Logo';
import { useAuth } from '../context/AuthContext';
import InitialsAvatar from './InitialsAvatar';

const NAV_SECTIONS = [
  {
    title: 'Influencer',
    items: [
      { to: '/deals', label: 'deals', icon: Handshake, roles: ['manager', 'talent', 'talent_manager'] },
      { to: '/pitching', label: 'pitching', icon: Send, roles: ['manager', 'talent', 'talent_manager'] },
      { to: '/templates', label: 'templates', icon: FileEdit, roles: ['manager', 'talent', 'talent_manager'] },
      { to: '/content-data', label: 'content data', icon: Film, roles: ['manager', 'talent', 'talent_manager'] },
      { to: '/pr-events', label: 'pr & events', icon: Megaphone, roles: ['manager', 'talent', 'talent_manager'] },
      { to: '/talent', label: 'talent', icon: Users, roles: ['manager', 'talent_manager'] },
      { to: '/brands', label: 'brands', icon: Building2, roles: ['manager', 'talent_manager'] },
      { to: '/reports', label: 'reports', icon: BarChart3, roles: ['manager', 'talent_manager'] },
    ],
  },
  {
    title: 'Agency',
    items: [
      { to: '/clients', label: 'clients', icon: Briefcase, roles: ['manager'] },
      { to: '/social', label: 'social', icon: Grid3x3, roles: ['manager', 'social_manager', 'talent_manager'] },
      { to: '/mailers', label: 'mailers', icon: Mail, roles: ['manager', 'social_manager', 'talent_manager'] },
      { to: '/projects', label: 'projects', icon: ClipboardList, roles: ['manager', 'social_manager', 'talent_manager'] },
      { to: '/database', label: 'database', icon: Database, roles: ['manager', 'social_manager', 'talent_manager'] },
      { to: '/insights', label: 'insights', icon: LineChart, roles: ['manager', 'social_manager', 'talent_manager'] },
    ],
  },
  {
    title: 'Admin',
    items: [
      { to: '/todos', label: 'to-do', icon: StickyNote, roles: ['manager', 'talent', 'social_manager', 'talent_manager'] },
      { to: '/calendar', label: 'calendar', icon: CalendarDays, roles: ['manager', 'talent', 'social_manager', 'talent_manager'] },
      { to: '/invoicing', label: 'invoicing', icon: FileText, roles: ['manager', 'talent', 'talent_manager'] },
      { to: '/expenses', label: 'expenses', icon: Wallet, roles: ['manager', 'talent', 'social_manager', 'talent_manager'] },
      { to: '/finances', label: 'finances', icon: PiggyBank, roles: ['manager'] },
      { to: '/settings', label: 'settings', icon: Settings, roles: ['manager', 'talent_manager'] },
    ],
  },
];

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const sections = NAV_SECTIONS
    .map((s) => ({
      ...s,
      items: s.items.filter((n) => {
        // A user with social_access gets social-manager nav on top of their base role.
        const effectiveRoles = [user?.role];
        if (user?.social_access) effectiveRoles.push('social_manager');
        if (!n.roles.some((r) => effectiveRoles.includes(r))) return false;
        // Expenses is separate from hour-logging: managers/admins always see it;
        // can_log_hours only gates non-manager roles.
        if (n.to === '/expenses') {
          if (['manager', 'talent_manager'].includes(user?.role)) return true;
          return Boolean(user?.can_log_hours);
        }
        return true;
      }),
    }))
    .filter((s) => s.items.length > 0);

  return (
    <aside className="hidden md:flex md:w-60 lg:w-64 shrink-0 flex-col border-r border-slate-200 bg-white h-screen sticky top-0">
      <div className="px-5 py-5 border-b border-slate-100">
        <Logo size="md" />
      </div>

      <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
        {sections.map((section) => (
          <div key={section.title}>
            <div className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
              {section.title}
            </div>
            <div className="space-y-1">
              {section.items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive ? 'bg-mint-50 text-mint-700 font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`
                  }
                  data-testid={`nav-${label.replace(/[^a-z0-9]+/gi, '-')}`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="px-3 py-3 border-t border-slate-100">
        <div className="flex items-center gap-3 px-3 py-3">
          <InitialsAvatar name={user?.name} size={32} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.title || user?.email}</p>
          </div>
          <button onClick={async () => { await logout(); navigate('/login'); }} className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Sign out">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
